import moment from "moment";
import { useCallback, useMemo } from "react";
import { PastOnlyDateRangeEnum } from "types/topicflow";

import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import { ButtonSize } from "@components/button/button";
import Input from "@components/input/input";
import InputDatePicker from "@components/input/input-datepicker";
import DeprecatedSelect, { SelectOption } from "@components/select/select";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { dateRangeToDateArray } from "@helpers/helpers";

import { DateRangeType, ExtraDateRangeEnum, ProgramForm } from "./types";

const ComplianceProgramFormProgramPeriod = ({
  proposedComplianceProgram,
  onUpdateProposedComplianceProgram,
  selectedProgramPeriodRange,
  setSelectedProgramPeriodRange,
}: {
  proposedComplianceProgram: ProgramForm;
  onUpdateProposedComplianceProgram: (data: ProgramForm) => void;
  selectedProgramPeriodRange: DateRangeType;
  setSelectedProgramPeriodRange: (range: DateRangeType) => void;
}) => {
  const label = useLabel();
  const organization = currentOrganizationVar();

  const programPeriodOptions = useMemo(() => {
    const dateRangeOptions = Object.values(PastOnlyDateRangeEnum).map(
      (dateRange) => ({
        value: dateRange,
        label: label(dateRange, { capitalize: true }),
        description: dateRangeToDateArray({
          range: dateRange,
          quarterStartMonth: organization.quarterStartMonth,
        })
          .map((date) => moment(date).format("ll"))
          .join(" - "),
      })
    );
    return [
      {
        value: ExtraDateRangeEnum.none,
        label: "Not set",
      },
      ...dateRangeOptions,
      {
        value: ExtraDateRangeEnum.custom,
        label: "Custom date range",
      },
    ];
  }, [label, organization]);

  const handleChangeProgramPeriod = useCallback(
    (option: SelectOption<DateRangeType>) => {
      if (option.value === ExtraDateRangeEnum.none) {
        onUpdateProposedComplianceProgram({
          ...proposedComplianceProgram,
          periodStartDate: null,
          periodEndDate: null,
        });
      } else if (option.value !== ExtraDateRangeEnum.custom) {
        const dates = dateRangeToDateArray({
          range: option.value,
          quarterStartMonth: organization.quarterStartMonth,
        });
        onUpdateProposedComplianceProgram({
          ...proposedComplianceProgram,
          periodStartDate: dates[0],
          periodEndDate: dates[1],
        });
      }
      setSelectedProgramPeriodRange(option.value);
    },
    [
      onUpdateProposedComplianceProgram,
      organization,
      proposedComplianceProgram,
      setSelectedProgramPeriodRange,
    ]
  );

  return proposedComplianceProgram.ongoing ? (
    <div className="flex gap-2 items-center justify-between">
      <div className="text-sm">Starts</div>
      <Input
        type="number"
        className="w-24"
        min={0}
        step={1}
        aria-label="Compliance program due date offset"
        value={proposedComplianceProgram.startDatePeriodStartOffset ?? ""}
        onChange={(e) =>
          onUpdateProposedComplianceProgram({
            ...proposedComplianceProgram,
            startDatePeriodStartOffset: Math.trunc(Number(e.target.value)),
          })
        }
      />
      <div className="text-sm">days after start date</div>
      <TopicflowIcon
        type="rightArrow"
        className="text-gray-500 w-4 h-4 shrink-0"
      />
      <div className="text-sm">Ends</div>
      <Input
        type="number"
        className="w-24"
        min={0}
        step={1}
        aria-label="Compliance program due date offset"
        value={proposedComplianceProgram.startDatePeriodEndOffset ?? ""}
        onChange={(e) =>
          onUpdateProposedComplianceProgram({
            ...proposedComplianceProgram,
            startDatePeriodEndOffset: Math.trunc(Number(e.target.value)),
          })
        }
      />
      <div className="text-sm">days after start date</div>
    </div>
  ) : (
    <div className="w-full">
      <DeprecatedSelect<DateRangeType>
        onChange={handleChangeProgramPeriod}
        options={programPeriodOptions}
        value={selectedProgramPeriodRange}
        buttonProps={{
          size: ButtonSize.large,
        }}
      />

      {selectedProgramPeriodRange === ExtraDateRangeEnum.custom && (
        <div className="mt-2 flex gap-2 items-center">
          <InputDatePicker
            date={proposedComplianceProgram.periodStartDate}
            placeholderText="Start date"
            wrapperClassName="flex-1"
            onChangeDate={(date) =>
              onUpdateProposedComplianceProgram({
                ...proposedComplianceProgram,
                periodStartDate: date
                  ? moment(date).format("YYYY-MM-DD")
                  : null,
              })
            }
            ariaLabelledBy="Compliance program period start date"
          />
          <TopicflowIcon type="rightArrow" className="text-gray-500 w-4 h-4" />
          <InputDatePicker
            date={proposedComplianceProgram.periodEndDate}
            placeholderText="End date"
            wrapperClassName="flex-1"
            onChangeDate={(date) =>
              onUpdateProposedComplianceProgram({
                ...proposedComplianceProgram,
                periodEndDate: date ? moment(date).format("YYYY-MM-DD") : null,
              })
            }
            ariaLabelledBy="Compliance program period end date"
          />
        </div>
      )}
    </div>
  );
};

export default ComplianceProgramFormProgramPeriod;
